/* Gentle entrances, reference card tilt and shared, visibility-aware magic. */
(() => {
  if (window.HbnJourney) return;
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const sections = new Map();
  const cards = new Map();
  const animations = new Set();
  const cardSelector = 'body.hbn-site :is(#MainContent, #search-modal, #header-component) :is(.product-card, .resource-card[data-resource-type="product"])';
  let collectionRoots = [];
  let collectionObserver = null;
  let collectionFrame = 0;
  const footerTrails = new Map();
  // Increase trail visibility by 30%, keeping the same density and short fade.
  const footerTrailOpacity = .42 * 1.3;
  class FooterTrail {
    constructor(footer) {
      this.footer = footer;
      this.abort = new AbortController();
      this.finePointer = matchMedia('(hover: hover) and (pointer: fine)');
      this.parts = [];
      this.visible = true;
      this.canvas = document.createElement('canvas');
      this.canvas.className = 'hbn-footer-trail';
      this.canvas.setAttribute('aria-hidden', 'true');
      this.ctx = this.canvas.getContext('2d');
      if (!this.ctx) return;
      footer.appendChild(this.canvas);
      this.tick = this.tick.bind(this);
      const options = { passive: true, signal: this.abort.signal };
      footer.addEventListener('pointermove', event => this.move(event), options);
      footer.addEventListener('pointerleave', () => { this.lastPoint = null; }, options);
      document.addEventListener('visibilitychange', () => this.clear(), options);
      motion.addEventListener('change', () => this.clear(), options);
      this.finePointer.addEventListener('change', () => this.clear(), options);
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.clear());
        this.resizeObserver.observe(footer);
      } else window.addEventListener('resize', () => this.clear(), options);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting;
          if (!this.visible) this.clear();
        });
        this.observer.observe(footer);
      }
    }
    move(event) {
      if (!this.ctx || motion.matches || !this.finePointer.matches || document.hidden || !this.visible) return;
      if (event.pointerType !== 'mouse' || event.buttons || event.target.closest('a, button, input, textarea, select, summary')) return;
      const now = performance.now();
      const rect = this.footer.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const x = event.clientX - rect.left, y = event.clientY - rect.top;
      if (this.lastPoint && (now - this.lastPoint.at < 65 || Math.hypot(x - this.lastPoint.x, y - this.lastPoint.y) < 10)) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      if (this.width !== rect.width || this.height !== rect.height || this.dpr !== dpr) {
        this.width = rect.width; this.height = rect.height; this.dpr = dpr;
        this.canvas.width = Math.round(rect.width * dpr);
        this.canvas.height = Math.round(rect.height * dpr);
        this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      }
      this.lastPoint = { x, y, at: now };
      const colors = ['#ecd7b8', '#d6bde9', '#c7ddd9'];
      this.parts.push({ x, y, born: now, life: 900 + Math.random() * 800,
        radius: .8 + Math.random() * 1.2, drift: (Math.random() - .5) * 22,
        rise: 10 + Math.random() * 15, color: colors[Math.floor(Math.random() * colors.length)], star: Math.random() < .18 });
      if (this.parts.length > 24) this.parts.shift();
      if (!this.frame) this.frame = requestAnimationFrame(this.tick);
    }
    tick(now) {
      this.frame = 0;
      if (motion.matches || document.hidden || !this.visible || !this.finePointer.matches) { this.clear(); return; }
      const ctx = this.ctx;
      ctx.clearRect(0, 0, this.width, this.height);
      ctx.shadowBlur = 0;
      this.parts = this.parts.filter(p => now - p.born < p.life);
      this.parts.forEach(p => {
        const progress = Math.max(0, (now - p.born) / p.life);
        const x = p.x + p.drift * progress, y = p.y - p.rise * progress;
        const r = p.radius * (1 - progress * .4);
        ctx.globalAlpha = footerTrailOpacity * Math.pow(1 - progress, 1.5);
        ctx.fillStyle = p.color;
        ctx.beginPath();
        if (p.star) {
          for (let i = 0; i < 8; i++) {
            const angle = i * Math.PI / 4, radius = i % 2 ? r * .5 : r * 1.8;
            const px = x + Math.cos(angle) * radius, py = y + Math.sin(angle) * radius;
            if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
          }
          ctx.closePath();
        } else ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.globalAlpha = 1;
      // No animation loop when the pointer trail has dissipated.
      if (this.parts.length) this.frame = requestAnimationFrame(this.tick);
    }
    clear() {
      cancelAnimationFrame(this.frame); this.frame = 0;
      this.parts = []; this.lastPoint = null;
      this.ctx?.clearRect(0, 0, this.width || 0, this.height || 0);
    }
    destroy() {
      this.clear(); this.abort.abort();
      this.observer?.disconnect(); this.resizeObserver?.disconnect(); this.canvas.remove();
    }
  }
  const decorateFooter = footer => {
    if (!footerTrails.has(footer)) footerTrails.set(footer, new FooterTrail(footer));
    if (footer.querySelector('.hbn-footer-magic')) return;
    const particles = document.createElement('span');
    particles.className = 'hbn-footer-magic';
    particles.setAttribute('aria-hidden', 'true');
    for (let i = 0; i < 5; i++) particles.appendChild(document.createElement('span'));
    footer.appendChild(particles);
  };
  const mountGlimpseLinks = () => {
    const main = document.querySelector('body.hbn-product-page #MainContent');
    if (!main) return;
    const heading = main.querySelector('.hbn-story-glimpse__heading');
    const language = document.documentElement.lang.toLowerCase().split('-')[0];
    const label = { en: 'Take a peek inside the story', ru: 'Заглянуть внутрь истории', lv: 'Ieskatīties stāstā' }[language] || 'Take a peek inside the story';
    if (heading && !heading.id) heading.id = 'hbn-story-glimpse-heading';
    main.querySelectorAll('[data-hbn-personalize-cta]').forEach(wrapper => {
      let link = wrapper.querySelector('.hbn-product-glimpse-link');
      if (!heading) { link?.remove(); return; }
      if (!link) {
        link = document.createElement('a');
        link.className = 'hbn-product-glimpse-link';
        link.innerHTML = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 6c-3-2-6-2-9-1v14c3-1 6-1 9 1 3-2 6-2 9-1V5c-3-1-6-1-9 1Zm0 0v14" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg><span></span>';
        link.addEventListener('click', event => {
          if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          const target = document.getElementById(link.hash.slice(1));
          if (!target) return;
          event.preventDefault();
          const headerHeight = document.querySelector('#header-component')?.getBoundingClientRect().height || 0;
          const top = Math.max(0, target.getBoundingClientRect().top + window.scrollY - headerHeight - 24);
          if (!target.hasAttribute('tabindex')) {
            target.tabIndex = -1;
            target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true });
          }
          target.focus({ preventScroll: true });
          window.scrollTo({ top, behavior: motion.matches ? 'auto' : 'smooth' });
        });
        wrapper.appendChild(link);
      }
      link.href = '#' + heading.id;
      link.querySelector('span').textContent = label;
    });
  };
  const resetCard = (card, state) => {
    cancelAnimationFrame(state.frame);
    state.frame = 0;
    state.rect = null;
    card.style.removeProperty('--hbn-card-x');
    card.style.removeProperty('--hbn-card-y');
  };
  const mountCards = () => {
    // Filters and pagination may remove cards without a Shopify editor unload event.
    cards.forEach((state, card) => {
      if (!card.isConnected || !card.matches(cardSelector)) {
        resetCard(card, state); state.abort.abort(); cards.delete(card);
      }
    });
    document.querySelectorAll(cardSelector).forEach(card => {
      if (cards.has(card)) return;
      const state = { frame: 0, rect: null, abort: new AbortController() };
      const options = { passive: true, signal: state.abort.signal };
      // Cache the untransformed rectangle to keep the tilt stable under the cursor.
      card.addEventListener('pointerenter', () => { state.rect = card.getBoundingClientRect(); }, options);
      card.addEventListener('pointermove', event => {
        if (motion.matches || document.hidden || event.pointerType !== 'mouse') return;
        if (card.closest('[product-grid-view="zoom-out"]')) return;
        const rect = state.rect || card.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const x = Math.max(-1, Math.min(1, (event.clientX - rect.left) / rect.width * 2 - 1));
        const y = Math.max(-1, Math.min(1, (event.clientY - rect.top) / rect.height * 2 - 1));
        state.x = -y * 4.2; state.y = x * 6;
        if (!state.frame) state.frame = requestAnimationFrame(() => {
          state.frame = 0;
          card.style.setProperty('--hbn-card-x', `${state.x.toFixed(2)}deg`);
          card.style.setProperty('--hbn-card-y', `${state.y.toFixed(2)}deg`);
        });
      }, options);
      ['pointerleave', 'pointercancel', 'blur'].forEach(type => card.addEventListener(type, () => resetCard(card, state), options));
      cards.set(card, state);
    });
  };
  const observeCollection = () => {
    const roots = [...document.querySelectorAll('#MainContent, #search-modal, #header-component')];
    if (roots.length === collectionRoots.length && roots.every((root, i) => root === collectionRoots[i])) return;
    collectionObserver?.disconnect();
    cancelAnimationFrame(collectionFrame);
    collectionFrame = 0;
    collectionRoots = roots;
    if (!roots.length || !('MutationObserver' in window)) return;
    // Native filtering and recommendations replace their cards asynchronously.
    // Style/hover changes and image swaps do not remount the page.
    collectionObserver = new MutationObserver(records => {
      let changed = false;
      records.forEach(record => {
        if (record.type === 'attributes') {
          cards.forEach((state, card) => { if (record.target.contains(card)) resetCard(card, state); });
          return;
        }
        const relevant = node => node.nodeType === 1 && (
          node.matches('.product-card, .resource-card, results-list, .shopify-section') ||
          node.querySelector('.product-card, .resource-card, results-list')
        );
        if ([...record.addedNodes, ...record.removedNodes].some(relevant)) changed = true;
      });
      if (changed && !collectionFrame) collectionFrame = requestAnimationFrame(() => {
        collectionFrame = 0;
        mount();
      });
    });
    roots.forEach(root => collectionObserver.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['product-grid-view'] }));
  };
  let backToTop = null;
  let scrollFrame = 0;
  const updateBackToTop = () => {
    scrollFrame = 0;
    if (!backToTop?.isConnected) return;
    const visible = window.scrollY > Math.max(500, window.innerHeight * .7);
    backToTop.classList.toggle('is-visible', visible);
    backToTop.tabIndex = visible ? 0 : -1;
    backToTop.setAttribute('aria-hidden', String(!visible));
  };
  const scheduleBackToTop = () => {
    if (!scrollFrame) scrollFrame = requestAnimationFrame(updateBackToTop);
  };
  const mountBackToTop = () => {
    if (backToTop?.isConnected) return;
    backToTop = document.createElement('button');
    backToTop.id = 'hbn-back-to-top';
    backToTop.type = 'button';
    backToTop.className = 'hbn-back-to-top';
    const language = document.documentElement.lang.toLowerCase().split('-')[0];
    const label = { en: 'Back to top', ru: 'Вернуться наверх', lv: 'Atpakaļ uz augšu' }[language] || 'Back to top';
    backToTop.setAttribute('aria-label', label);
    backToTop.title = label;
    backToTop.innerHTML = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 19V6M6.5 11.5 12 6l5.5 5.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg><span class="hbn-back-to-top__spark" aria-hidden="true"></span>';
    backToTop.addEventListener('click', () => {
      // Move keyboard focus away from the control before it disappears near the top.
      const main = document.getElementById('MainContent');
      if (main) {
        const hadTabIndex = main.hasAttribute('tabindex');
        if (!hadTabIndex) main.setAttribute('tabindex', '-1');
        main.focus({ preventScroll: true });
        if (!hadTabIndex) main.addEventListener('blur', () => main.removeAttribute('tabindex'), { once: true });
      }
      window.scrollTo({ top: 0, behavior: motion.matches ? 'instant' : 'smooth' });
    });
    document.body.appendChild(backToTop);
    updateBackToTop();
  };
  const animate = item => {
    if (motion.matches || document.hidden || !item.animate) return;
    const effect = item.animate(
      [{ opacity: .55, transform: 'translateY(18px)' }, { opacity: 1, transform: 'none' }],
      { duration: 650, easing: 'cubic-bezier(.2,.7,.3,1)' }
    );
    animations.add(effect);
    effect.finished.catch(() => {}).finally(() => animations.delete(effect));
  };
  const refresh = () => {
    sections.forEach((state, element) => {
      element.toggleAttribute('data-hbn-journey-visible', state.visible && !document.hidden && !motion.matches);
    });
    if (motion.matches || document.hidden) cards.forEach((state, card) => resetCard(card, state));
  };
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      const state = sections.get(entry.target);
      if (!state) return;
      state.visible = entry.isIntersecting;
      if (!state.visible) cards.forEach((cardState, card) => { if (entry.target.contains(card)) resetCard(card, cardState); });
      if (state.visible && !state.revealed) {
        state.revealed = true;
        if (entry.target.matches('.ui-test-product-list')) {
          const title = entry.target.querySelector('.section-resource-list__header');
          if (title) animate(title);
        } else if (!entry.target.matches('[data-hbn-magic]') && !entry.target.querySelector('[data-hbn-magic]')) {
          const title = entry.target.querySelector('.hbn-recognition-tabs__header, .section-content-wrapper > .text-block, .blog-posts > .text-block, .section-content-wrapper > header');
          if (title) animate(title);
        }
      }
    });
    refresh();
  }, { threshold: .05 }) : null;
  const mount = () => {
    footerTrails.forEach((trail, footer) => {
      if (!footer.isConnected) { trail.destroy(); footerTrails.delete(footer); }
    });
    mountBackToTop();
    mountGlimpseLinks();
    sections.forEach((_state, section) => {
      if (!section.isConnected) { observer?.unobserve(section); sections.delete(section); }
    });
    document.querySelectorAll('body.hbn-site #MainContent > .shopify-section, body > footer').forEach(section => {
      if (section.matches('footer')) decorateFooter(section);
      if (sections.has(section)) return;
      sections.set(section, { visible: !observer, revealed: false });
      observer?.observe(section);
    });
    mountCards();
    observeCollection();
    refresh();
  };
  document.addEventListener('visibilitychange', refresh);
  window.addEventListener('scroll', scheduleBackToTop, { passive: true });
  window.addEventListener('resize', scheduleBackToTop, { passive: true });
  window.addEventListener('pageshow', scheduleBackToTop);
  window.addEventListener('resize', () => cards.forEach((state, card) => resetCard(card, state)), { passive: true });
  motion.addEventListener('change', () => {
    if (motion.matches) animations.forEach(animation => animation.cancel());
    refresh();
  });
  document.addEventListener('shopify:section:load', mount);
  document.addEventListener('shopify:section:unload', event => {
    sections.forEach((_state, section) => {
      if (section === event.target || event.target.contains(section)) {
        observer?.unobserve(section); sections.delete(section);
      }
    });
    cards.forEach((state, card) => {
      if (event.target.contains(card)) { resetCard(card, state); state.abort.abort(); cards.delete(card); }
    });
  });
  window.HbnJourney = { mount };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true });
  else mount();
})();
